import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AdjustmentsHorizontalIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, LifebuoyIcon, PlusIcon, ChevronDownIcon,
  CheckIcon, KeyIcon, BoltIcon, ShieldCheckIcon, DocumentTextIcon, UserPlusIcon, CreditCardIcon,
  CameraIcon, IdentificationIcon, FingerPrintIcon, SparklesIcon, ClockIcon, PaintBrushIcon, XMarkIcon,
} from '@heroicons/react/24/outline'
import { API_BASE_URL } from '../../config/api'
import { isCommunity } from '../../config/edition'
import type { ApiKey, DeveloperStats, ApiActivity } from './types'
import type { Wallet } from '../../lib/billingApi'
import { formatMoney } from '../../lib/money'
import { verificationsCovered } from '../../lib/billing'
import {
  Card, CardHeader, PillButton, PillLink, Dropdown, Avatar, AvatarStack, EmptyState, SegmentedBar, Progress,
  StatusTag, verificationStatus, Skeleton,
} from '../dashboard/primitives'
import { FolderStatCard, FolderUpdateCard } from '../dashboard/FolderStatCard'
import { HeaderStats, PageHead } from '../dashboard/Layout'
import { LineChart, BarChart, TrendLine, ProgressRing, Legend } from '../dashboard/charts'
import { greetingLine } from '../dashboard/greeting'

// ─── Types (subset of /api/developer/analytics) ──────────────────────
export interface DailyVolume { date: string; total: number; verified: number; failed: number; success_rate: number }
export interface FunnelStep { step: string; count: number; percentage: number }
export interface DevAnalytics {
  daily_volume: DailyVolume[]
  funnel: FunnelStep[]
  quota: { used: number; limit: number | null }
  daily_webhooks?: Array<{ date: string; delivered: number; failed: number }>
}
interface ActivityResponse {
  statistics: { total_requests: number; successful_requests: number; failed_requests: number; pending_requests: number; manual_review_requests: number }
  recent_activities: ApiActivity[]
  session_outcomes: Record<string, string>
}

export const STEP_META: Record<string, { label: string; icon: JSX.Element }> = {
  initialized: { label: 'Session started', icon: <SparklesIcon /> },
  front_uploaded: { label: 'Front of ID', icon: <IdentificationIcon /> },
  back_uploaded: { label: 'Back of ID', icon: <DocumentTextIcon /> },
  live_captured: { label: 'Liveness selfie', icon: <CameraIcon /> },
  completed: { label: 'Completed', icon: <ShieldCheckIcon /> },
}

export const shortDay = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { weekday: 'short' })
}
const longDay = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i
const timeAgo = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}
const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0)

type Period = 'month' | 'all'

export function DeveloperOverview({ token, stats, apiKeys, profileName, isOperator, hasOrgAdmin, wallet, refreshVersion, onOpenSettings }: {
  token: string
  stats: DeveloperStats | null
  apiKeys: ApiKey[]
  profileName?: string | null
  isOperator: boolean
  hasOrgAdmin: boolean | null
  wallet: Wallet | null
  refreshVersion: number
  onOpenSettings: () => void
}) {
  const [analytics, setAnalytics] = useState<DevAnalytics | null>(null)
  const [activity, setActivity] = useState<ActivityResponse | null>(null)
  const [webhookCount, setWebhookCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<Period>('month')
  const [showVerified, setShowVerified] = useState(true)
  const [showFailed, setShowFailed] = useState(true)
  const [search, setSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    const opts: RequestInit = { headers: token === 'session' ? {} : { Authorization: `Bearer ${token}` }, credentials: 'include' }
    const get = (p: string) => fetch(`${API_BASE_URL}${p}`, opts).then(r => (r.ok ? r.json() : null)).catch(() => null)
    setLoading(true)
    Promise.all([get('/api/developer/analytics'), get('/api/developer/activity'), get('/api/developer/webhooks')]).then(([a, act, wh]) => {
      if (cancelled) return
      setAnalytics(a)
      setActivity(act)
      setWebhookCount(wh ? (wh.webhooks ?? []).length : null)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [token, refreshVersion])

  // ── Period-scoped totals (month = /stats, all = /activity statistics) ──
  const totals = useMemo(() => {
    if (period === 'all' && activity?.statistics) {
      const s = activity.statistics
      return { total: s.total_requests, verified: s.successful_requests, failed: s.failed_requests, review: (s.manual_review_requests ?? 0) + (s.pending_requests ?? 0) }
    }
    if (!stats) return null
    const ext = stats as DeveloperStats & { pending_requests?: number; manual_review_requests?: number }
    return { total: stats.monthly_usage ?? stats.total_requests, verified: stats.successful_requests, failed: stats.failed_requests, review: (ext.manual_review_requests ?? 0) + (ext.pending_requests ?? 0) }
  }, [period, stats, activity])

  const daily = analytics?.daily_volume ?? []
  const hasDaily = daily.some(d => d.total > 0)
  const today = daily[daily.length - 1]
  const activeKeys = apiKeys.filter(k => k.is_active && k.status !== 'expired')

  // ── Last verification (from activity endpoints carrying a session id) ──
  const lastSession = useMemo(() => {
    for (const a of activity?.recent_activities ?? []) {
      const m = a.endpoint?.match(UUID_RE)
      if (m) return { id: m[0], at: a.timestamp, status: activity?.session_outcomes?.[m[0]] }
    }
    return null
  }, [activity])

  // ── Success-rate trend (days with traffic only) ──
  const rateDays = daily.filter(d => d.total > 0)
  const rateNow = totals && totals.total > 0 ? pct(totals.verified, totals.total) : null
  const rateDelta = rateDays.length >= 2 ? Math.round((rateDays[rateDays.length - 1].success_rate - rateDays[rateDays.length - 2].success_rate) * 100) / 100 : null

  // ── Onboarding checklist (all real signals) ──
  const tasks = [
    { label: 'Create an API key', done: apiKeys.length > 0, to: '/developer/api-keys', icon: <KeyIcon /> },
    { label: 'Create a live key', done: apiKeys.some(k => !k.is_sandbox), to: '/developer/api-keys', icon: <BoltIcon /> },
    { label: 'Run your first verification', done: (activity?.statistics?.total_requests ?? stats?.total_requests ?? 0) > 0, to: '/demo', icon: <FingerPrintIcon /> },
    { label: 'Get a verified result', done: (activity?.statistics?.successful_requests ?? 0) > 0, to: '/demo', icon: <ShieldCheckIcon /> },
    { label: 'Add a webhook endpoint', done: (webhookCount ?? 0) > 0, to: '/developer/webhooks', icon: <DocumentTextIcon /> },
    ...(!isOperator ? [{ label: 'Invite an organisation admin', done: hasOrgAdmin === true, onClick: onOpenSettings, icon: <UserPlusIcon /> }] : []),
    ...(wallet?.enabled && !isOperator ? [{ label: 'Top up your wallet', done: wallet.balance_cents > 0, to: '/developer/billing', icon: <CreditCardIcon /> }] : []),
  ]
  const doneCount = tasks.filter(t => t.done).length
  const onboardingPct = Math.round((doneCount / tasks.length) * 100)

  const recent = (activity?.recent_activities ?? []).filter(a => !search || `${a.method} ${a.endpoint} ${a.status_code}`.toLowerCase().includes(search.toLowerCase())).slice(0, 5)
  const funnel = analytics?.funnel ?? []
  const funnelMax = Math.max(1, ...funnel.map(f => f.count))
  const quota = analytics?.quota
  const startedStep = funnel.find(f => f.step === 'initialized')
  const completedStep = funnel.find(f => f.step === 'completed')
  const completion = startedStep && startedStep.count > 0 ? { started: startedStep.count, done: completedStep?.count ?? 0 } : null

  const downloadCsv = () => {
    const rows = [['date', 'total', 'verified', 'failed', 'success_rate']]
    daily.forEach(d => rows.push([d.date, String(d.total), String(d.verified), String(d.failed), String(d.success_rate)]))
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `saflens-verifications-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const lineSeries = [
    ...(showVerified ? [{ key: 'verified', label: 'Verified', tone: 'accent' as const, values: daily.map(d => d.verified) }] : []),
    ...(showFailed ? [{ key: 'failed', label: 'Failed', tone: 'ink' as const, values: daily.map(d => d.failed) }] : []),
  ]

  return (
    <div className="sf-fade">
      <PageHead
        title={greetingLine(profileName)}
        sub={period === 'month' ? 'Here is how your verifications are doing this month.' : 'Here is your verification activity since you joined.'}
        actions={<>
          <HeaderStats items={[
            { label: 'Total', value: totals ? totals.total.toLocaleString() : '—' },
            { label: 'Verified', value: totals ? totals.verified.toLocaleString() : '—' },
            { label: 'In review', value: totals ? totals.review.toLocaleString() : '—' },
          ]} />
          <PillLink to="/demo" variant="primary" icon={<PlusIcon />}>New verification</PillLink>
        </>}
      />

      {/* toolbar */}
      <div className="sf-toolbar">
        <Dropdown trigger={({ toggle }) => <PillButton icon={<AdjustmentsHorizontalIcon />} onClick={toggle}>Filter</PillButton>}>
          {() => (<>
            <div className="sf-dd-label">Chart series</div>
            <label><input type="checkbox" checked={showVerified} onChange={e => setShowVerified(e.target.checked)} style={{ accentColor: '#f97316' }} /> Verified</label>
            <label><input type="checkbox" checked={showFailed} onChange={e => setShowFailed(e.target.checked)} style={{ accentColor: '#f97316' }} /> Failed</label>
          </>)}
        </Dropdown>
        <Dropdown trigger={({ toggle }) => <PillButton iconRight={<ChevronDownIcon />} onClick={toggle}>{period === 'month' ? 'Monthly' : 'All time'}</PillButton>}>
          {close => (<>
            <button type="button" className={period === 'month' ? 'on' : ''} onClick={() => { setPeriod('month'); close() }}>Monthly</button>
            <button type="button" className={period === 'all' ? 'on' : ''} onClick={() => { setPeriod('all'); close() }}>All time</button>
          </>)}
        </Dropdown>
        <PillButton icon={<ArrowDownTrayIcon />} onClick={downloadCsv} disabled={!daily.length}>Download data</PillButton>
        <span className="sf-toolbar-spacer" />
        {searchOpen ? (
          <div className="sf-input-wrap" style={{ width: 240 }}>
            <MagnifyingGlassIcon />
            <input className="sf-input" style={{ height: 42, borderRadius: 999 }} autoFocus placeholder="Filter recent activity…" value={search} onChange={e => setSearch(e.target.value)} onBlur={() => { if (!search) setSearchOpen(false) }} />
          </div>
        ) : (
          <PillButton iconOnly aria-label="Search activity" title="Search activity" icon={<MagnifyingGlassIcon />} onClick={() => setSearchOpen(true)} />
        )}
        <PillLink to="/developer/page-builder" icon={<PaintBrushIcon />}>Page builder</PillLink>
        <PillLink to="/docs" icon={<LifebuoyIcon />}>Support</PillLink>
      </div>

      {wallet?.enabled && wallet.low_balance && !isOperator && (
        <div className="sf-banner">
          <span className="sf-roundicon"><CreditCardIcon /></span>
          <div className="sf-banner-body">
            <b>Your wallet balance is low — {formatMoney(wallet.balance_cents)}</b>
            <span>Live verifications stop when the balance can't cover {formatMoney(wallet.price_per_verification_cents)} per check.</span>
          </div>
          <PillLink to="/developer/billing" variant="primary" size="sm">Top up</PillLink>
        </div>
      )}

      {/* folder stat cards */}
      <div className="sf-grid c4">
        <FolderStatCard
          label="Verified"
          tone="accent"
          loading={!totals}
          value={totals?.verified.toLocaleString()}
          caption="Success rate"
          captionValue={rateNow === null ? 'No checks yet' : `${rateNow}%`}
          to="/developer/analytics"
        />
        <FolderStatCard
          label="API keys"
          visual={apiKeys.length
            ? <AvatarStack names={apiKeys.map(k => k.name || k.key_preview)} max={4} size={46} />
            : <span style={{ color: 'var(--sf-mid)', fontWeight: 500 }}>No keys yet</span>}
          caption="Active keys"
          captionValue={`${activeKeys.length} of ${apiKeys.length}${apiKeys.some(k => !k.is_sandbox) ? ' · live enabled' : ''}`}
          to="/developer/api-keys"
        />
        {wallet?.enabled && !isOperator ? (
          <FolderStatCard
            label="Wallet"
            value={formatMoney(wallet.balance_cents)}
            caption="Covers"
            captionValue={`${verificationsCovered(wallet.balance_cents, wallet.price_per_verification_cents)?.toLocaleString() ?? '—'} verifications`}
            to="/developer/billing"
          />
        ) : (
          <FolderStatCard
            label="In review"
            loading={!totals}
            value={totals?.review.toLocaleString()}
            caption="Failed"
            captionValue={totals ? totals.failed.toLocaleString() : '—'}
          />
        )}
        <FolderUpdateCard
          label="Last verification"
          icon={<FingerPrintIcon />}
          title={lastSession ? <>Session {lastSession.id.slice(0, 8)}{lastSession.status ? ` · ${verificationStatus(lastSession.status).label}` : ''}</> : undefined}
          to="/developer/api-keys"
          updatedAt={lastSession?.at}
          emptyText={loading ? 'Loading…' : 'No sessions yet'}
        />
      </div>

      {/* activity chart + today's info */}
      <div className="sf-grid main sf-section">
        <Card>
          <CardHeader
            title="Verification activity"
            sub="Verified vs failed, last 7 days"
            action={<PillLink to="/developer/analytics" variant="outline" size="sm">See details</PillLink>}
          />
          {loading ? <Skeleton h={240} r={20} /> : hasDaily && lineSeries.length ? (
            <>
              <LineChart labels={daily.map(d => shortDay(d.date))} series={lineSeries} height={250} ariaLabel="Daily verified and failed verifications" />
              <div style={{ marginTop: 10 }}><Legend items={lineSeries.map(s => ({ label: s.label, tone: s.tone }))} /></div>
            </>
          ) : (
            <EmptyState icon={<FingerPrintIcon />} title={hasDaily ? 'All series hidden' : 'No verifications in the last 7 days'} action={!hasDaily && <PillLink to="/demo" size="sm" variant="dark">Try a verification</PillLink>}>
              {hasDaily ? 'Turn a series back on from Filter.' : 'Run a test session from the demo or call the API — activity shows up here instantly.'}
            </EmptyState>
          )}
        </Card>

        <Card tone="accent" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="sf-roundicon" style={{ background: 'rgba(255,255,255,0.22)', color: '#fff' }}><ClockIcon /></span>
            <span className="sf-chip white">Today's info</span>
          </div>
          <div>
            <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
              {loading ? '…' : `${(today?.total ?? 0).toLocaleString()} verification${today?.total === 1 ? '' : 's'} today`}
            </div>
            <p style={{ margin: '10px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: 14.5 }}>
              {today && today.total > 0
                ? `${today.verified} verified and ${today.failed} failed so far${today.date ? ` on ${longDay(today.date)}` : ''}.`
                : 'No sessions yet today. Share your hosted verification link or call the API to get started.'}
              {quota && quota.limit !== null && !isCommunity ? ` You've used ${quota.used} of ${quota.limit} monthly checks.` : ''}
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
            {[
              { l: 'Verified', v: today?.verified ?? 0 },
              { l: 'Failed', v: today?.failed ?? 0 },
              { l: 'This week', v: daily.reduce((a, d) => a + d.total, 0) },
            ].map(x => (
              <div key={x.l} style={{ background: 'rgba(255,255,255,0.16)', borderRadius: 18, padding: '12px 14px' }}>
                <div className="sf-num" style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>{loading ? '…' : x.v.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>{x.l}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 8, color: 'rgba(255,255,255,0.85)' }}>
              <span>{quota && quota.limit !== null && !isCommunity ? 'Monthly quota' : 'Success rate this month'}</span>
              <b style={{ color: '#fff' }}>
                {quota && quota.limit !== null && !isCommunity ? `${pct(quota.used, quota.limit)}%` : rateNow === null ? '—' : `${rateNow}%`}
              </b>
            </div>
            {quota && quota.limit !== null && !isCommunity
              ? <SegmentedBar value={quota.used} max={quota.limit} segments={14} />
              : <SegmentedBar value={rateNow ?? 0} max={100} segments={14} />}
          </div>
        </Card>
      </div>

      {/* trend / bars / ring */}
      <div className="sf-grid c3 sf-section">
        <Card>
          <CardHeader title="Success rate" sub={period === 'month' ? 'This month' : 'All time'} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="sf-kpi">{rateNow === null ? '—' : `${Math.round(rateNow)}%`}</span>
            {rateDelta !== null && <span className={`sf-delta ${rateDelta < 0 ? 'down' : ''}`}>{rateDelta >= 0 ? '+' : ''}{rateDelta}%</span>}
          </div>
          <p className="sf-card-sub" style={{ margin: '8px 0 14px' }}>{rateDelta !== null ? 'vs the previous active day' : 'Daily trend appears after two active days'}</p>
          {rateDays.length > 1 ? <TrendLine values={rateDays.map(d => d.success_rate)} labels={rateDays.map(d => shortDay(d.date))} formatValue={v => `${Math.round(v)}%`} height={92} /> : <Skeleton h={92} r={18} style={{ animation: 'none' }} />}
        </Card>

        <Card>
          <CardHeader title="Daily volume" sub="Sessions per day · highlighted above average" action={<PillButton variant="outline" size="sm" onClick={downloadCsv} disabled={!daily.length}>Export</PillButton>} />
          {loading ? <Skeleton h={200} r={20} /> : hasDaily
            ? <BarChart labels={daily.map(d => shortDay(d.date))} values={daily.map(d => d.total)} height={210} valueLabel="sessions" tooltipSub={i => longDay(daily[i].date)} ariaLabel="Daily verification volume" />
            : <EmptyState icon={<BoltIcon />} title="No volume yet">Bars appear as sessions come in.</EmptyState>}
        </Card>

        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader title="Completion rate" sub="Sessions started that reached a decision" />
          <div style={{ display: 'grid', placeItems: 'center', flex: 1, paddingBottom: 6 }}>
            {completion ? (
              <ProgressRing value={completion.done} max={completion.started} size={190}>
                <b>{Math.round(pct(completion.done, completion.started))}%</b>
                <span>{completion.done.toLocaleString()} of {completion.started.toLocaleString()} sessions</span>
              </ProgressRing>
            ) : loading ? <Skeleton h={190} w={190} r={999} /> : <EmptyState icon={<ClockIcon />} title="No sessions yet">The ring fills as applicants finish their checks.</EmptyState>}
          </div>
        </Card>
      </div>

      {/* funnel / activity / onboarding */}
      <div className="sf-grid c3 sf-section">
        <Card>
          <CardHeader title="Verification funnel" sub="Where applicants drop off" action={<PillLink to="/developer/analytics" variant="outline" size="sm">See details</PillLink>} />
          {funnel.length && funnel.some(f => f.count > 0) ? (
            <div className="sf-list">
              {funnel.map(f => {
                const meta = STEP_META[f.step] ?? { label: f.step.replace(/_/g, ' '), icon: <SparklesIcon /> }
                return (
                  <div className="sf-row" key={f.step} style={{ alignItems: 'flex-start' }}>
                    <span className={`sf-roundicon ${f.step === 'completed' ? 'solid' : 'accent'}`}>{meta.icon}</span>
                    <div className="sf-row-main">
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span className="sf-row-title">{meta.label}</span>
                        <span className="sf-row-end"><b style={{ color: 'var(--sf-ink)' }}>{f.count.toLocaleString()}</b> · {Math.round(f.percentage)}%</span>
                      </div>
                      <Progress value={f.count} max={funnelMax} dark={f.step === 'completed'} />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : <EmptyState icon={<SparklesIcon />} title="No funnel data yet">Each step lights up as applicants move through your flow.</EmptyState>}
        </Card>

        <Card>
          <CardHeader title="Recent API activity" sub={search ? `Filtered by “${search}”` : 'Latest requests from your keys'} action={<PillLink to="/developer/api-keys" variant="outline" size="sm">See details</PillLink>} />
          {recent.length ? (
            <div className="sf-list">
              {recent.map((a, i) => {
                const sid = a.endpoint?.match(UUID_RE)?.[0]
                const outcome = sid ? activity?.session_outcomes?.[sid] : undefined
                const ok = a.status_code < 400
                return (
                  <div className="sf-row" key={`${a.timestamp}-${i}`}>
                    <Avatar name={a.method} size={40} tone={ok ? 0 : 2} />
                    <div className="sf-row-main">
                      <div className="sf-row-title">{a.endpoint.replace(UUID_RE, ':id')}</div>
                      <div className="sf-row-sub">{a.status_code} · {a.response_time_ms}ms · {timeAgo(a.timestamp)}</div>
                    </div>
                    {outcome ? <StatusTag {...verificationStatus(outcome)}>{verificationStatus(outcome).label}</StatusTag> : <StatusTag tone={ok ? 'neutral' : 'bad'}>{ok ? 'OK' : 'Error'}</StatusTag>}
                  </div>
                )
              })}
            </div>
          ) : (
            <EmptyState icon={<BoltIcon />} title={search ? 'No matching requests' : 'No API calls yet'}>
              {search ? 'Try another search term.' : 'Requests made with your API keys appear here.'}
            </EmptyState>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <h3 className="sf-card-title">Onboarding</h3>
              <span style={{ fontSize: 34, fontWeight: 500, letterSpacing: '-0.04em' }} className="sf-num">{onboardingPct}%</span>
            </div>
            <p className="sf-card-sub" style={{ margin: '2px 0 14px' }}>Finish setup to go live with confidence</p>
            <SegmentedBar value={doneCount} max={tasks.length} segments={tasks.length * 3} />
          </Card>
          <Card tone="dark" style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <h3 className="sf-card-title">Setup tasks</h3>
              <span className="sf-chip" style={{ background: 'rgba(255,255,255,0.12)', color: '#fff' }}>{doneCount}/{tasks.length}</span>
            </div>
            <div className="sf-list">
              {tasks.map(t => {
                const inner = (
                  <>
                    <span className="sf-roundicon" style={{ width: 32, height: 32 }}>{t.icon}</span>
                    <div className="sf-row-main"><div className="sf-row-title" style={{ fontWeight: 500, opacity: t.done ? 0.6 : 1, textDecoration: t.done ? 'line-through' : 'none' }}>{t.label}</div></div>
                    <span className={`sf-check ${t.done ? 'on' : ''}`}><CheckIcon strokeWidth={3} /></span>
                  </>
                )
                return 'to' in t && t.to
                  ? <Link key={t.label} to={t.to} className="sf-row" style={{ padding: '7px 0' }}>{inner}</Link>
                  : <button key={t.label} type="button" onClick={'onClick' in t ? t.onClick : undefined} className="sf-row" style={{ padding: '7px 0', background: 'none', border: 0, borderTop: '1px solid rgba(255,255,255,0.1)', color: 'inherit', cursor: 'pointer', width: '100%', textAlign: 'left' }}>{inner}</button>
              })}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

export function DismissibleTeamBanner({ onInvite, onDismiss }: { onInvite: () => void; onDismiss: () => void }) {
  return (
    <div className="sf-banner soft">
      <span className="sf-roundicon solid"><UserPlusIcon /></span>
      <div className="sf-banner-body">
        <b>Set up your team</b>
        <span>Invite an Organisation Admin to approve, reject and review verifications without your developer credentials.</span>
      </div>
      <PillButton variant="primary" size="sm" onClick={onInvite}>Invite admin</PillButton>
      <PillButton variant="grey" size="sm" iconOnly aria-label="Dismiss" icon={<XMarkIcon />} onClick={onDismiss} />
    </div>
  )
}
